import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Alert,
  CircularProgress,
  Modal,
  ModalDialog,
  Grid,
  Checkbox
} from "@mui/joy";
import { useNavigate } from 'react-router-dom';
import { useWindowBasketStore } from '../hooks/useWindowBasket';
import { useOrderStore } from '../hooks/useOrder';
import { sendOrderEmail, signIn, isSignedIn } from '../utils/graphService';
import WindowOrderSummary from './WindowOrderSummary';
import { convertWindowItemsForOrder } from '../types/windowOrder';
import SupplierSelector from './SupplierSelector';
import { directSupabase } from '../lib/supabaseClient';
import { validateWindowImages } from '../utils/windowHelpers';
import type { Database } from '../types/supabase';
type Supplier = Database['public']['Tables']['suppliers']['Row'];

interface WindowCheckoutFormData {
  supplierIds: string[]; }
// Import supplier types
type EmailStatus = 'idle' | 'sending' | 'success' | 'error';

const WindowCheckout: React.FC = () => {
  const { items, getTotalPrice, clearItems, customerReference } = useWindowBasketStore();
  const { createOrder } = useOrderStore();
  const [formData, setFormData] = useState<WindowCheckoutFormData>({
    supplierIds: []
  });
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderReference, setOrderReference] = useState('');
  const navigate = useNavigate();
  
  // Load suppliers and filter to only window suppliers
  const [windowSuppliers, setWindowSuppliers] = useState<Supplier[]>([]);
  
  // Email sending state
  const [emailStatus, setEmailStatus] = useState<EmailStatus>('idle');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Create Draft in Outlook state
  const [isCreatingDraft, setIsCreatingDraft] = useState(false);
  
  // For draft email status
  const [draftStatus, setDraftStatus] = useState<{
    status: 'idle' | 'creating' | 'success' | 'error';
    message: string;
  }>({
    status: 'idle',
    message: '',
  });
  
  // Add a state to track if the user wants to proceed with missing images
  const [ignoreMissingImages, setIgnoreMissingImages] = useState(false);
  
  // Add a state to track images that failed to load
  const [failedImages, setFailedImages] = useState<string[]>([]);
  
  // Load suppliers on component mount
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const { data, error } = await directSupabase
          .from('suppliers')
          .select('*')
          .order('supplier_name', { ascending: true });
        
        if (error) {
          throw new Error(error.message);
        }
        
        const windowOnly = (data || []).filter((supplier: Supplier) => supplier.category === 'Windows');
        setWindowSuppliers(windowOnly);
      } catch (error) {
        console.error('Failed to fetch suppliers:', error);
        setWindowSuppliers([]);
      }
    };
    fetchSuppliers();
  }, []);
  
  // Debug: Log all items and highlight any missing imagePath
  useEffect(() => {
    if (items.length > 0) {
      console.log('WindowCheckout: Current basket items:', items);
      const missingImageItems = items.filter(item => !item.imagePath);
      if (missingImageItems.length > 0) {
        console.warn('Some window items are missing imagePath:', missingImageItems);
      }
    }
  }, [items]);
  
  // Add a function to check if all window images can load - now using our helper
  const validateWindowImagesInBasket = async () => {
    const failedImageIds = await validateWindowImages(items);
    setFailedImages(failedImageIds);
    return failedImageIds.length === 0;
  };
  
  // Check images when items change
  useEffect(() => {
    if (items.length > 0) {
      validateWindowImagesInBasket();
    }
  }, [items, validateWindowImagesInBasket]);
  
  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};
    
    if (!customerReference.trim()) errors.customerReference = 'Customer reference is required';
    if (formData.supplierIds.length === 0) errors.supplierIds = 'Please select at least one supplier';
    
    // Validate window images only if user hasn't chosen to ignore the issue
    if (failedImages.length > 0 && !ignoreMissingImages) {
      errors.images = `${failedImages.length} window(s) have missing or invalid images`;
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Find selected suppliers from our loaded suppliers list
      const selectedSuppliers = windowSuppliers.filter(
        supplier => formData.supplierIds.includes(supplier.id)
      );

      if (selectedSuppliers.length === 0) {
        throw new Error('No suppliers selected');
      }

      // For order storage, we'll just use the first selected supplier as primary
      const primarySupplier = selectedSuppliers[0];

      // Create the order object
      const customerInfo = {
        name: customerReference,
        email: '',
        phone: '',
        customerReference
      };
      
      const subtotal = getTotalPrice();
      const vat = subtotal * 0.2;
      const total = subtotal + vat;

      // Convert window items to basket items for the order system
      const convertedItems = convertWindowItemsForOrder(items);

      // Create the order in our store 
      const order = createOrder(
        customerInfo, 
        convertedItems, 
        subtotal, 
        vat, 
        total, 
        primarySupplier,
        selectedSuppliers // Pass all selected suppliers
      );
      
      // Set order reference for confirmation page
      setOrderReference(order.orderNumber);
      
      // Clear the basket
      clearItems();
      
      // Send emails to all selected suppliers
      setEmailStatus('sending');
      try {
        // Check if signed in and handle sign in if needed
        const isCurrentlySignedIn = await isSignedIn();
        if (!isCurrentlySignedIn) {
          const signInSuccess = await signIn();
          if (!signInSuccess) {
            throw new Error('Please sign in to your Microsoft account to send emails');
          }
        }

        // Filter suppliers that have email addresses
        const suppliersWithEmail = selectedSuppliers.filter(supplier => supplier.email);
        
        if (suppliersWithEmail.length === 0) {
          throw new Error('No suppliers have email addresses configured');
        }

        await Promise.all(suppliersWithEmail.map(supplier =>
          sendOrderEmail(order, supplier.email!, true)
        ));
        setEmailStatus('success');
      } catch (error) {
        console.error('Failed to send emails:', error);
        setEmailStatus('error');
        setEmailError(error instanceof Error ? error.message : 'Unknown error sending emails');
      }
      
      // Mark order as placed
      setOrderPlaced(true);
    } catch (error) {
      console.error('Failed to submit order:', error);
      alert(error instanceof Error ? error.message : 'Failed to submit order');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Modify the hasMissingImages function to use the tracked failed images
  const hasMissingImages = () => {
    return failedImages.length > 0 || items.some(item => !item.imagePath);
  };
  
  const shouldDisableDraft = () => {
    if (items.length === 0) return true;
    if (hasMissingImages() && !ignoreMissingImages) return true;
    return false;
  };

  const shouldDisableSend = () => {
    const hasSupplierAndItems = windowSuppliers.length > 0 &&
      formData.supplierIds.length > 0 &&
      items.length > 0;

    if (!hasSupplierAndItems) return true;
    if (hasMissingImages() && !ignoreMissingImages) return true;
    return false;
  };
  
  // If basket is empty, redirect to designer
  if (items.length === 0 && !orderPlaced) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography level="h4" mb={2}>Your window quote is empty</Typography>
        <Typography level="body-md" mb={3}>Add some windows to proceed with requesting a price</Typography>
        <Button variant="outlined" onClick={() => navigate('/window-designer')}>Back to Window Designer</Button>
      </Box>
    );
  }

  // If order is placed, show confirmation
  if (orderPlaced) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography level="h3" color="success" mb={2}>
          Price Request Submitted!
        </Typography>
        <Alert color="success" sx={{ mb: 3 }}>
          Your request reference number is: <strong>{orderReference}</strong>
        </Alert>
        
        {/* Email status display */}
        {emailStatus === 'sending' && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
            <CircularProgress size="sm" />
            <Typography level="body-md" sx={{ ml: 2 }}>
              Sending price request to suppliers...
            </Typography>
          </Box>
        )}
        
        {emailStatus === 'success' && (
          <Alert color="success" sx={{ mb: 3 }}>
            Price request has been sent to the selected suppliers.
          </Alert>
        )}
        
        {emailStatus === 'error' && (
          <Box sx={{ mb: 3 }}>
            <Alert color="danger" sx={{ mb: 2 }}>
              Error sending price request: {emailError}
            </Alert>
            <Button 
              variant="solid" 
              color="danger" 
              onClick={() => {
                setEmailStatus('sending');
                handleSubmit({} as React.FormEvent);
              }}
              startDecorator={(emailStatus as string) === 'sending' ? <CircularProgress size="sm" /> : undefined}
              disabled={(emailStatus as string) === 'sending'}
            >
              Try Sending Again
            </Button>
          </Box>
        )}
        
        <Typography level="body-md" mb={3}>
          Thank you for your request. {emailStatus !== 'error' ? 'Your price request has been sent to the selected suppliers.' : ''}
        </Typography>
        <Button variant="outlined" component="a" href="/window-designer">Back to Window Designer</Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ p: 3 }}>
      <Typography level="h3" mb={3}>Window Price Request</Typography>
      
      <Grid container spacing={3}>
        {/* Order summary - make it the main feature */}
        <Grid xs={12} md={8} sx={{ mb: { xs: 3, md: 0 } }}>
          <Box 
            sx={{ 
              border: '1px solid', 
              borderColor: 'divider',
              borderRadius: 'md',
              mb: 3
            }}
          >
            <WindowOrderSummary />
          </Box>
        </Grid>
        
        {/* Simplified form */}
        <Grid xs={12} md={4}>
          <Box 
            component="form" 
            onSubmit={handleSubmit}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 'md',
              p: 2
            }}
          >
            <Typography level="h4" mb={2}>Request Your Price</Typography>
            
            {formErrors.customerReference && (
              <Alert color="danger" sx={{ mb: 3 }}>
                {formErrors.customerReference}
              </Alert>
            )}
            
            {/* Display customer reference as non-editable at checkout */}
            <Box sx={{ mb: 3 }}>
              <Typography level="title-sm">Customer Reference</Typography>
              <Typography level="body-md" fontWeight="bold">{customerReference}</Typography>
            </Box>
            
            {/* Warning for missing window images */}
            {hasMissingImages() && (
              <Alert 
                endDecorator={
                  <Checkbox
                    checked={ignoreMissingImages}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setIgnoreMissingImages(e.target.checked)}
                    label="Continue anyway"
                  />
                }
              >
                <strong>Warning:</strong> One or more windows are missing a preview image. 
                This may result in placeholder images in the email. Proceed only if you're comfortable with this.
              </Alert>
            )}
            
            <Typography level="title-sm" mb={1}>Select Window Supplier(s) *</Typography>
            <Typography level="body-xs" sx={{ mb: 1, color: 'neutral.600' }}>
              A price request will be sent to each selected supplier
            </Typography>
            <SupplierSelector
              value={formData.supplierIds[0] || ''}
              onChange={(id) => {
                setFormData(prev => ({
                  ...prev,
                  supplierIds: id ? [id] : []
                }));
                setFormErrors(prev => ({
                  ...prev,
                  supplierIds: ''
                }));
              }}
              supplierType="Windows"
            />
            {formErrors.supplierIds && (
              <Typography level="body-xs" color="danger">{formErrors.supplierIds}</Typography>
            )}
            
            {/* Show selected supplier */}
            {formData.supplierIds.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <Typography level="body-xs">Selected supplier:</Typography>
                <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                  {formData.supplierIds.map(id => {
                    const supplier = windowSuppliers.find(s => s.id === id);
                    return supplier ? (
                      <Typography component="li" level="body-xs" key={id}>
                        {supplier.supplier_name}
                      </Typography>
                    ) : null;
                  })}
                </Box>
              </Box>
            )}
            
            {/* Draft status feedback */}
            {draftStatus.status !== 'idle' && (
              <Box sx={{ mt: 2, mb: 2 }}>
                {draftStatus.status === 'creating' && (
                  <Alert startDecorator={<CircularProgress size="sm" />}>
                    {draftStatus.message}
                  </Alert>
                )}
                {draftStatus.status === 'success' && (
                  <Alert>
                    {draftStatus.message}
                  </Alert>
                )}
                {draftStatus.status === 'error' && (
                  <Alert>
                    {draftStatus.message}
                  </Alert>
                )}
              </Box>
            )}
            
            {/* Action buttons */}
            <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {/* Empty Basket Button */}
              <Button
                variant="soft"
                color="danger"
                onClick={() => {
                  if (window.confirm('Are you sure you want to empty your basket?')) {
                    clearItems();
                    // Navigate back to the designer after emptying
                    navigate('/window-designer');
                  }
                }}
              >
                Empty Basket
              </Button>
            
              {/* Continue Shopping Button */}
              <Button
                variant="outlined"
                color="neutral"
                onClick={() => navigate('/window-designer?step=types')}
              >
                Continue Shopping
              </Button>
              
              {/* Create Draft in Outlook Button */}
              <Button
                variant="outlined"
                color="primary"
                disabled={isCreatingDraft || shouldDisableDraft()}
                startDecorator={isCreatingDraft ? <CircularProgress size="sm" /> : null}
                onClick={async () => {
                  try {
                    setIsCreatingDraft(true);
                    setDraftStatus({
                      status: 'creating',
                      message: 'Preparing draft email...'
                    });
                    
                    // Check if signed in and handle sign in if needed
                    const isCurrentlySignedIn = await isSignedIn();
                    if (!isCurrentlySignedIn) {
                      setDraftStatus({
                        status: 'creating',
                        message: 'Signing in to Microsoft account...'
                      });
                      const signInSuccess = await signIn();
                      if (!signInSuccess) {
                        throw new Error('Please sign in to your Microsoft account to create a draft email');
                      }
                    }
                    
                    // Supplier is optional for drafts (you can pick a recipient later in Outlook)
                    const supplier = windowSuppliers.find(s => formData.supplierIds.includes(s.id)) || undefined;
                    const recipient = supplier?.email || '';
                    
                    setDraftStatus({
                      status: 'creating',
                      message: 'Generating email content and attachments...'
                    });
                    
                    // Create the order object with the validated supplier
                    const order = createOrder(
                      { name: customerReference, email: '', phone: '', customerReference },
                      convertWindowItemsForOrder(items),
                      getTotalPrice(),
                      getTotalPrice() * 0.2,
                      getTotalPrice() * 1.2,
                      supplier,
                      supplier ? [supplier] : undefined
                    );
                    
                    setDraftStatus({
                      status: 'creating',
                      message: `Creating draft email${supplier?.supplier_name ? ` for ${supplier.supplier_name}` : ''}...`
                    });
                    
                    await sendOrderEmail(order, recipient, true, true); // createDraft = true
                    
                    setDraftStatus({
                      status: 'success',
                      message: `Draft email created! You can find it in your Outlook Drafts folder.`
                    });
                    
                    // Reset after 5 seconds
                    setTimeout(() => {
                      setDraftStatus({
                        status: 'idle',
                        message: ''
                      });
                    }, 5000);
                  } catch (error) {
                    console.error('Error creating draft:', error);
                    setDraftStatus({
                      status: 'error',
                      message: error instanceof Error ? error.message : 'Failed to create draft email'
                    });
                  } finally {
                    setIsCreatingDraft(false);
                  }
                }}
              >
                Create Draft in Outlook
              </Button>

              {/* Send Email Directly Button */}
              <Button
                variant="solid"
                color="primary"
                disabled={isSubmitting || shouldDisableSend()}
                startDecorator={isSubmitting ? <CircularProgress size="sm" /> : null}
                onClick={handleSubmit}
              >
                Send Price Request
              </Button>
            </Box>
          </Box>
        </Grid>
      </Grid>
      
      {/* Processing order modal */}
      <Modal open={isSubmitting}>
        <ModalDialog sx={{ p: 4, maxWidth: 400 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <CircularProgress size="lg" />
            <Typography level="body-lg" sx={{ mt: 2 }}>
              Processing your price request...
            </Typography>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
};

export default WindowCheckout; 